import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";

// const API_BASE = `http://${window.location.hostname}:8000`;
const API_BASE = process.env.REACT_APP_API_URL;
// const API_BASE = 'https://spradley-website.onrender.com';
// const MEDIA_BASE = `https://spradley-website-media.s3.amazonaws.com/images`;
const MEDIA_BASE = `${API_BASE}/media`;
const S3_BASE = "https://spradley-website-media.s3.amazonaws.com";
// const CLOUDFRONT_BASE = "https://dya1gu5h2u70i.cloudfront.net"

export function getMediaUrl(mediaId) {
  if (!mediaId) return null;

  const isVideo = mediaId.endsWith(".mp4") || mediaId.endsWith(".webm");
  const prefix = isVideo ? "videos" : "images";

  return `${S3_BASE}/${prefix}/${mediaId}`; 
}

export function useMediaUrl(mediaId, options={}) {
  const icon = options.icon ?? false;

  return useQuery({
    queryKey: ["media-url", mediaId, icon],
    enabled: options.enabled ?? !!mediaId,
    queryFn: async () => {
      const res = await fetch(`${MEDIA_BASE}/${mediaId}?icon=${icon}`);
      if (!res.ok) {
        throw new Error("Failed to fetch media URL");
      }
      return (await res.json()).url;
    },
    staleTime: 50 * 60 * 1000,
  });
}

// export function useUploadMedia() {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: async (file) => {
//       const authRes = await fetch(`${MEDIA_BASE}/upload`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({
//           filename: file.name,
//           content_type: file.type,
//         }),
//       });
//       if (!authRes.ok) {
//         const msg = await authRes.json();
//         throw new Error(msg.detail || "Failed to authorize upload.");
//       }
//       const upload = await authRes.json();

//       const formData = new FormData();
//       Object.entries(upload.fields).forEach(([key, value]) => {
//         formData.append(key, value);
//       });
//       formData.append("file", file);
      
//       const s3Res = await fetch(upload.url, {
//         method: "POST",
//         body: formData,
//       });
//       if (!s3Res.ok) {
//         throw new Error("Failed to upload file to S3.");
//       }

//       return {
//         media_id: upload.media_id,
//         original_filename: upload.original_filename,
//       };
//     },

//     onSuccess: () => {
//       queryClient.invalidateQueries({
//         queryKey: ["media"],
//       });
//     },
//   });
// }
export function useUploadMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const original = await uploadFile(file);

      let icon = null;

      if (file.type.startsWith("image/")) {
        const iconFile = await createImageIcon(file);
        icon = await uploadFile(iconFile, original.media_id, true);
      }

      return {
        media_id: original.media_id,
        // icon_id: icon?.media_id ?? null,
        original_filename: original.original_filename,
      };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["media"],
      });
    },
  });
}

export function useDeleteMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId) => {
      const res = await fetch(`${MEDIA_BASE}/${mediaId}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const msg = await res.json();
        throw new Error(msg.detail || "Failed to delete media");
      }

      return res.json();
    },

    onSuccess: () => {
      queryClient.invalidateQueries(["media"]);
    },
  });
}

async function createImageIcon(file) {
  if (!file.type.startsWith("image/")) {
    return null;
  }

  const bitmap = await createImageBitmap(file);

  const maxSize = 200;
  const scale = Math.min(maxSize / bitmap.width, maxSize / bitmap.height, 1);

  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", 0.85);
  });

  return new File(
    [blob],
    `${file.name.replace(/\.[^/.]+$/, "")}.jpg`,
    { type: "image/jpeg" }
  );
}

async function uploadFile(file, media_name = undefined, icon_tf = undefined) {
  const authRes = await fetch(`${MEDIA_BASE}/upload`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type,
      media_id: media_name,
      icon: icon_tf
    }),
  });

  if (!authRes.ok) {
    const msg = await authRes.json();
    throw new Error(msg.detail || "Failed to authorize upload.");
  }

  const upload = await authRes.json();

  const formData = new FormData();

  Object.entries(upload.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  formData.append("file", file);

  const s3Res = await fetch(upload.url, {
    method: "POST",
    body: formData,
  });

  if (!s3Res.ok) {
    throw new Error("Failed to upload file to S3.");
  }

  return {
    media_id: upload.media_id,
    original_filename: upload.original_filename,
  };
}